import { useState } from 'react';
import { Badge, Button, Panel, ScreenHeader, StateBox, Table, humanize, type Column, type CoreBadgeTone } from '@garnet/ui';
import type { InspectorStanding } from '@garnet/domain';
import { useAsync } from '../useAsync.js';
import type { AdminApi, AdminInspector, AdminProfessional, CredentialState } from '../data/types.js';
import { credentialTone } from '../data/shape.js';
import { StatStrip, NameCell, TagRow } from '../components/AdminBits.js';

const STANDING_TONE: Record<InspectorStanding, CoreBadgeTone> = {
  active: 'b-pass',
  probation: 'b-warn',
  suspended: 'b-fail',
};

const CRED_RANK: Record<CredentialState, number> = { expired: 2, expiring: 1, valid: 0 };

function worstCredential(creds: AdminInspector['credentials']): CredentialState {
  return creds.reduce<CredentialState>((w, c) => (CRED_RANK[c.state] > CRED_RANK[w] ? c.state : w), 'valid');
}

/**
 * Inspector & professional ops (PRD §10.1, §20, §29). Onboard, credential, route and monitor
 * standing for field inspectors and labs, and the professional-services panel (notaries,
 * lawyers, translators). Routing can be paused locally; an expired credential or suspended
 * standing keeps an actor out of rotation.
 */
export function Inspectors({ api }: { api: AdminApi }) {
  const inspectors = useAsync(() => api.getInspectors());
  const professionals = useAsync(() => api.getProfessionals());
  const [paused, setPaused] = useState<ReadonlySet<string>>(new Set());

  if (inspectors.loading || professionals.loading) return <StateBox kind="loading" title="Loading inspector ops" />;
  if (inspectors.error || !inspectors.data || professionals.error || !professionals.data)
    return <StateBox kind="error" title="Could not load inspector ops">{inspectors.error ?? professionals.error}</StateBox>;

  const insp = inspectors.data;
  const pros = professionals.data;
  const togglePause = (id: string): void =>
    setPaused((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const inspectorColumns: Column<AdminInspector>[] = [
    { key: 'name', header: 'Inspector / lab', render: (i) => <NameCell name={i.name} sub={`${i.id} · ${humanize(i.kind)}`} /> },
    { key: 'standing', header: 'Standing', render: (i) => <Badge tone={STANDING_TONE[i.standing]}>{i.standing}</Badge> },
    { key: 'methods', header: 'Methods', render: (i) => <TagRow tags={i.coverage.methods.map(humanize)} /> },
    { key: 'juris', header: 'Coverage', mono: true, render: (i) => i.coverage.jurisdictions.map((j) => j.toUpperCase()).join(' ') },
    {
      key: 'creds',
      header: 'Credentials',
      render: (i) => {
        const w = worstCredential(i.credentials);
        return <Badge tone={credentialTone(w)}>{w}</Badge>;
      },
    },
    { key: 'sla', header: 'SLA', mono: true, render: (i) => `${Math.round(i.rating.sla_hit_rate * 100)}%` },
    {
      key: 'disputes',
      header: 'Disputes',
      mono: true,
      render: (i) => (
        <span style={{ color: i.open_disputes > 0 ? 'var(--status-warn-text)' : 'var(--fg-3)' }}>{i.open_disputes}</span>
      ),
    },
    {
      key: 'route',
      header: 'Routing',
      render: (i) => {
        const routable = i.standing === 'active' && worstCredential(i.credentials) !== 'expired';
        if (!routable) return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-3)' }}>out of rotation</span>;
        const isPaused = paused.has(i.id);
        return (
          <Button sm variant={isPaused ? 'primary' : 'ghost'} onClick={() => togglePause(i.id)}>
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
        );
      },
    },
  ];

  const proColumns: Column<AdminProfessional>[] = [
    { key: 'name', header: 'Professional', render: (p) => <NameCell name={p.name} sub={`${p.id} · ${humanize(p.type)}`} /> },
    { key: 'standing', header: 'Standing', render: (p) => <Badge tone={STANDING_TONE[p.status]}>{p.status}</Badge> },
    { key: 'where', header: 'Jurisdiction', mono: true, render: (p) => `${p.jurisdiction.toUpperCase()} · ${p.country}` },
    { key: 'langs', header: 'Languages', render: (p) => <TagRow tags={p.languages} /> },
  ];

  return (
    <div className="screen">
      <ScreenHeader eyebrow="Inspector ops" title="Inspectors, labs & professionals" lead="Onboard, credential, route and monitor standing for field inspectors and labs, plus the professional-services desk. An expired credential or suspended standing keeps an actor out of rotation." />
      <StatStrip
        stats={[
          { label: 'Inspectors & labs', value: insp.length },
          { label: 'Active', value: insp.filter((i) => i.standing === 'active').length, tone: 'pass' },
          { label: 'Probation', value: insp.filter((i) => i.standing === 'probation').length, tone: 'warn' },
          { label: 'Open disputes', value: insp.reduce((n, i) => n + i.open_disputes, 0), tone: 'warn' },
          { label: 'Professionals', value: pros.length },
        ]}
      />
      <Panel title="Inspectors & labs" right={<span className="metatext">onboard · credential · route · standing</span>} flush bodyStyle={{ maxHeight: 520, overflow: 'auto' }}>
        <Table columns={inspectorColumns} rows={insp} rowKey={(i) => i.id} />
      </Panel>
      <Panel title="Professional services" right={<span className="metatext">notary · lawyer · translator · assessor</span>} flush>
        <Table columns={proColumns} rows={pros} rowKey={(p) => p.id} />
      </Panel>
    </div>
  );
}
